import { ComponentPropsWithoutRef, ElementType, FC, MouseEventHandler, ReactNode } from 'react';
import { Breakpoint, styled, useMediaQuery, useTheme } from '@mui/material';
import colors from '@o2/ui/theme/colors';
import Tooltip, { TooltipProps } from './Tooltip';
import { Box } from '@o2/ui';
import palette from '@o2/ui/theme/palette';
import { darken } from '@mui/material/styles';

const StyledButton = styled('button', {
  name: 'AppBarButton',
  slot: 'root',
})(({ theme }) => ({
  background: 'none',
  color: palette.primary.main,
  border: 'none',
  textDecoration: 'none',
  cursor: 'pointer',
  padding: theme.spacing(2, 2.5),
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  fontFamily: theme.typography.fontFamily,
  fontSize: 16,
  fontWeight: 500,
  transition: theme.transitions.create(['background-color'], { duration: theme.transitions.duration.standard }),
  whiteSpace: 'nowrap',
  gap: theme.spacing(2),
  '&:hover': {
    backgroundColor: colors.transparentWhite[100],
    color: palette.primary.main,
    textDecoration: 'underline',
    textUnderlineOffset: '0.3em',
  },
  '& svg': {
    fontSize: 24,
    verticalAlign: 'middle',
  },
  '&:hover svg': {
    color: darken(palette.primary.main, 0.3),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 1.5),
  },
}));

const StyledBadge = styled('div', {
  name: 'AppBarButton',
  slot: 'badge',
})(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: colors.basic.white,
  borderRadius: '50%',
  width: 20,
  height: 20,
  position: 'absolute',
  top: -7,
  right: -7,
  '& div': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.warning['500'],
    borderRadius: '50%',
    width: 16,
    height: 16,
    '& div': {
      backgroundColor: colors.basic.white,
      borderRadius: '50%',
      width: 4,
      height: 4,
    },
  },
}));

/**
 * Badge for icon
 *
 * Divs are ok here. We need 3 circles ;)
 */
const Badge: FC = () => {
  return (
    <StyledBadge>
      <div>
        <div />
      </div>
    </StyledBadge>
  );
};

export type ButtonProps<T extends ElementType = 'button'> = {
  /** Button text / content */
  children?: ReactNode;

  /** Icon */
  icon?: ReactNode;

  /** Breakpoint to hide the text */
  breakpoint?: Breakpoint;

  onClick?: MouseEventHandler;

  /**
   * Tooltip content.
   *
   * Not working on touch screens.
   */
  tooltip?: ReactNode;

  /**
   * Props passed to the Tooltip component.
   */
  tooltipProps?: Omit<TooltipProps, 'title' | 'children' | 'ref'>;

  /** Show / hide badge **button must have icon set** */
  showBadge?: boolean;

  /**
   * What component to render this component as (inheriting props).
   * Useful when combining with e.g. react-router.
   */
  as?: T;
} & ComponentPropsWithoutRef<T>;

/**
 * Button for `AppBar.Controls`
 */
const Button = <T extends ElementType = 'button'>({
  showBadge,
  tooltip,
  icon,
  children,
  breakpoint = 'lg',
  tooltipProps,
  ...other
}: ButtonProps<T>) => {
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down(breakpoint));
  const btn = (
    <StyledButton {...other}>
      {icon && (
        <Box sx={{ position: 'relative' }}>
          {icon}
          {showBadge && <Badge />}
        </Box>
      )}
      {!(smallScreen && icon) && children}
    </StyledButton>
  );

  return tooltip ? (
    <Tooltip title={tooltip} {...tooltipProps}>
      {btn}
    </Tooltip>
  ) : (
    btn
  );
};

Button.displayName = 'AppBar.Button';

export default Button;
